<template>
  <CDialog
    width="640px"
    :visible.sync="currentVisible"
    :title="$t('Chart settings')"
    closeable
    :submittable="isAdmin"
    :submit-label="$t('Update')"
    persistent
    :loading="loading"
    @before-open="onBeforeOpen"
    @submit="onSubmit"
  >
    <div class="__PreferencesDialog">
      <CTabs v-model="tab" :tab-items="tabItems" dense />

      <CTabPanels v-model="tab">
        <CTabPanel name="chart">
          <PreferencesDialogChart
            :cost-type.sync="chartCostType"
            :currency.sync="chartCurrency"
            :default-chart-type.sync="chartDefaultChartType"
            :default-category.sync="chartDefaultCategory"
            :default-granularity.sync="chartDefaultGranularity"
            :default-range.sync="chartDefaultRange"
            :default-table-records-per-page.sync="chartDefaultTableRecordsPerPage"
            :total-stroke-type.sync="chartTotalStrokeType"
            :total-line-width.sync="chartTotalLineWidth"
            :total-color.sync="chartTotalColor"
            :non-grouping-series-count.sync="chartNonGroupingSeriesCount"
            :others-color.sync="chartOthersColor"
            :animation.sync="chartAnimation"
            :grouping.sync="chartGrouping"
            :scrollbar.sync="chartScrollbar"
            :category-options="categoryOptions"
          />
        </CTabPanel>

        <CTabPanel name="filter">
          <PreferencesDialogFilter
            :default-series-count.sync="filterDefaultSeriesCount"
            :default-series.sync="filterDefaultSeries"
          />
        </CTabPanel>

        <CTabPanel name="export">
          <PreferencesDialogExport
            :dataType.sync="exportDataType"
            :imageType.sync="exportImageType"
            :prefix.sync="exportPrefix"
          />
        </CTabPanel>
      </CTabPanels>
    </div>
  </CDialog>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import CDialog from '@/components/uikit/CDialog';
import CRow from '@/components/uikit/CRow';
import CCol from '@/components/uikit/CCol';
import CTitle from '@/components/uikit/CTitle';
import CTabs from '@/components/uikit/CTabs';
import CTabPanels from '@/components/uikit/CTabs/CTabPanels';
import CTabPanel from '@/components/uikit/CTabs/CTabPanel';
import CSection from '@/components/uikit/CSection';
import CSeparator from '@/components/uikit/CSeparator';
import CCheckBox from '@/components/uikit/CCheckBox';
import CSelector from '@/components/uikit/CSelector';
import CInputNumber from '@/components/uikit/CInputNumber';
import CItem from '@/components/uikit/CItem';
import CItemSection from '@/components/uikit/CItemSection';
import CItemLabel from '@/components/uikit/CItemLabel';
import PreferencesDialogChart from '@/components/CustomCharts/PreferencesDialogChart';
import PreferencesDialogFilter from '@/components/CustomCharts/PreferencesDialogFilter';
import PreferencesDialogExport from '@/components/CustomCharts/PreferencesDialogExport';

const messages = {
  en: {
    'Chart settings': 'Chart Settings',
    Update: 'Save',
    Chart: 'Chart',
    Filter: 'Filter',
    Export: 'Export',
  },
  ja: {
    'Chart settings': 'チャートの環境設定',
    Update: '更新する',
    Chart: 'チャート',
    Filter: 'フィルター',
    Export: 'エクスポート',
  },
};

const mapping = {
  // Chart
  chartCostType: 'valueKey',
  chartCurrency: 'currency',
  chartDefaultChartType: 'defaultChartType',
  chartDefaultCategory: 'category',
  chartDefaultGranularity: 'granularity',
  chartDefaultRange: 'defaultRange',
  chartDefaultTableRecordsPerPage: 'defaultTableRowsPerPage',
  chartTotalStrokeType: 'totalStrokeType',
  chartTotalLineWidth: 'totalLineWidth',
  chartTotalColor: 'totalColor',
  chartNonGroupingSeriesCount: 'nonGroupingSeriesCount',
  chartOthersColor: 'othersColor',
  chartAnimation: 'animated',
  chartGrouping: 'grouping',
  chartScrollbar: 'scrollbar',

  // Series
  filterDefaultSeriesCount: 'defaultSeriesCount',
  filterDefaultSeries: 'defaultSeries',

  // Export
  exportDataType: 'exportDataType',
  exportImageType: 'exportImageType',
  exportPrefix: 'exportPrefix',
};

export default Vue.extend({
  name: 'PreferencesDialog',
  i18n: { messages },
  components: {
    CDialog,
    CRow,
    CCol,
    CTitle,
    CTabs,
    CTabPanels,
    CTabPanel,
    CSection,
    CSeparator,
    CCheckBox,
    CSelector,
    CInputNumber,
    CItem,
    CItemSection,
    CItemLabel,
    PreferencesDialogChart,
    PreferencesDialogFilter,
    PreferencesDialogExport,
  },
  props: {
    value: {
      type: Boolean,
    },
    settings: {
      type: Object,
      default: () => {},
    },
    conditions: {
      type: Object,
      default: () => {},
    },
    categoryOptions: {
      type: Array,
      default: () => [],
      required: true,
    },
    isOnDashboard: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Tab
      tab: 'chart',
      tabItems: [
        { label: this.$t('Chart'), value: 'chart' },
        { label: this.$t('Filter'), value: 'filter' },
        { label: this.$t('Export'), value: 'export' },
      ],

      // Chart
      chartCostType: 'true_unblended_cost',
      chartCurrency: 'usd',
      chartDefaultChartType: 'line',
      chartDefaultCategory: 'service',
      chartDefaultGranularity: 'daily',
      chartDefaultRange: '30days',
      chartDefaultTableRecordsPerPage: 30,
      chartNonGroupingSeriesCount: 5,
      chartTotalStrokeType: 'solid',
      chartTotalLineWidth: 2,
      chartTotalColor: '#111111',
      chartOthersColor: '#111111',
      chartAnimation: true,
      chartGrouping: true,
      chartScrollbar: true,

      // Series
      filterDefaultSeriesCount: 3,
      filterDefaultSeries: [],

      // Export
      exportDataType: 'csv',
      exportImageType: 'png',
      exportPrefix: 'chart',
    };
  },
  computed: {
    ...mapState('personal', {
      loading: state => state.loading.put,
    }),

    ...mapGetters('rbac', {
      isResourceAllowed: 'isResourceAllowed',
    }),

    isAdmin() {
      return this.$can('Admin') && this.isResourceAllowed(['Admin'], 'account_id', this.selectedId);
    },

    currentVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
  },
  methods: {
    ...mapActions('personal', ['put']),

    onBeforeOpen() {
      for (const mappingKey in mapping) {
        const value = this.conditions[mapping[mappingKey]];
        Vue.set(this, mappingKey, value);
      }
    },

    async onSubmit() {
      const params = {};

      for (const mappingKey in mapping) {
        params[mapping[mappingKey]] = this[mappingKey];
      }

      // Case: daily
      // APIがデータレンジ2ヶ月間しかサポートしていないため
      // 2ヶ月以上のレンジの場合は自動でMTDに変更する。
      if (params.granularity === 'daily') {
        if (params.defaultRange !== 'mtd' && params.defaultRange !== 'lastMonth') {
          params.defaultRange = 'mtd';
        }
      }

      if (this.isOnDashboard) {
        this.$emit('update-preferences', params);
      } else {
        await this.put(params);
      }

      this.currentVisible = false;
      this.$emit('success');
    },
  },
});
</script>

<style lang="scss" scoped>
.__PreferencesDialog {
  height: 560px;
  overflow: auto;
}
</style>
